
import { ExtractionRule } from '../types';
import { JSONPath } from 'jsonpath-plus';

const castValue = (value: any, type?: string) => {
    if (value === undefined || value === null) return null;
    if (!type || type === 'auto') return value;

    switch (type) {
        case 'string': return String(value);
        case 'number': return Number(value);
        case 'boolean': return Boolean(value);
        case 'object': return typeof value === 'object' ? value : { value };
        case 'array': return Array.isArray(value) ? value : [value];
        default: return value;
    }
};

/**
 * Heuristic to detect content type for UI display
 */
export const detectContentType = (content: string): 'JSON' | 'XML' | 'Text' => {
    if (!content || typeof content !== 'string') return 'Text';
    const trimmed = content.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) return 'JSON';
    if (trimmed.startsWith('<')) return 'XML';
    return 'Text';
};

/**
 * Evaluate a JSONPath expression against parsed JSON data.
 * Uses jsonpath-plus which supports the full JSONPath spec including
 * filters ($..book[?(@.price<10)]), recursive descent, array slicing, etc.
 */
const evaluateJsonPath = (json: any, path: string): any => {
    // Normalize: if the user typed a simple dot-path without $, prepend $.
    let normalizedPath = path.trim();
    if (!normalizedPath.startsWith('$')) {
        normalizedPath = '$.' + normalizedPath;
    }

    const results = JSONPath({ path: normalizedPath, json, wrap: true });
    if (!results || results.length === 0) return undefined;
    // Return single value unwrapped, array if multiple matches
    return results.length === 1 ? results[0] : results;
};

/**
 * Evaluate an XPath expression against an XML document.
 * Uses the browser-native document.evaluate (XPath 1.0).
 */
const evaluateXPath = (doc: Document, expression: string): any => {
    // Try real XPath first
    try {
        const result = doc.evaluate(expression, doc, null, XPathResult.ANY_TYPE, null);

        switch (result.resultType) {
            case XPathResult.NUMBER_TYPE:
                return result.numberValue;
            case XPathResult.STRING_TYPE:
                return result.stringValue;
            case XPathResult.BOOLEAN_TYPE:
                return result.booleanValue;
            default: {
                // Node iterator — collect text content
                const values: string[] = [];
                let node = result.iterateNext();
                while (node) {
                    values.push(node.textContent || '');
                    node = result.iterateNext();
                }
                if (values.length === 0) return undefined;
                return values.length === 1 ? values[0] : values;
            }
        }
    } catch {
        // XPath failed — fall back to querySelector for CSS-style selectors
        try {
            const node = doc.querySelector(expression);
            return node ? node.textContent : undefined;
        } catch {
            return undefined;
        }
    }
};

export interface BodyPredicate {
  kind: 'jsonpath' | 'xpath' | 'regex';
  expression: string;
  op: 'exists' | 'equals' | 'matches';
  value?: string;
}

export interface BodyPredicateResult {
  matched: boolean;
  /** Populated only when the predicate could not be evaluated (e.g. regex did
   *  not compile, unparseable JSON/XML). Lets callers surface the cause instead
   *  of silently treating it as a non-match. */
  error?: string;
}

/**
 * Boolean predicate over a response body — the authoring-time approximation of
 * the executor's bodyPredicate matching (ERROR-HANDLING-UI-PLAN §6).
 *
 * Unlike extractData (extraction-to-variable, where no-match and an extracted
 * null both collapse to null and regex-compile errors are swallowed), this is
 * an explicit boolean: a no-match is `false`, and a compile/parse failure is
 * surfaced via `error` rather than masquerading as a non-match.
 */
export const evaluateBodyPredicate = (
  body: string | undefined | null,
  predicate: BodyPredicate,
): BodyPredicateResult => {
  const { kind, expression, op, value } = predicate;
  if (!expression || !expression.trim()) {
    return { matched: false, error: 'Empty expression' };
  }
  const content = typeof body === 'string' ? body : '';

  // Compare a single extracted value against the predicate's op/value.
  const compare = (extracted: any): boolean => {
    if (op === 'exists') return extracted !== undefined && extracted !== null;
    if (extracted === undefined || extracted === null) return false;
    const str = String(extracted);
    if (op === 'equals') return str === (value ?? '');
    if (op === 'matches') {
      try {
        return new RegExp(value ?? '').test(str);
      } catch {
        return false; // invalid value-regex caught by the caller's compile check below
      }
    }
    return false;
  };

  try {
    if (kind === 'jsonpath') {
      let json: any;
      try {
        json = JSON.parse(content);
      } catch {
        return { matched: false, error: 'Body is not valid JSON' };
      }
      const extracted = evaluateJsonPath(json, expression);
      if (op === 'matches' && value) {
        try { new RegExp(value); } catch (e) {
          return { matched: false, error: `Invalid regex value: ${(e as Error).message}` };
        }
      }
      // jsonpath may return an array of matches — "exists" is true if any; for
      // equals/matches, any matching element satisfies the predicate.
      if (op === 'exists') return { matched: extracted !== undefined && extracted !== null };
      const candidates = Array.isArray(extracted) ? extracted : [extracted];
      return { matched: candidates.some(compare) };
    }

    if (kind === 'xpath') {
      let doc: Document;
      try {
        const parser = new DOMParser();
        doc = parser.parseFromString(content, 'text/xml');
        if (doc.querySelector('parsererror')) {
          return { matched: false, error: 'Body is not valid XML' };
        }
      } catch {
        return { matched: false, error: 'Body is not valid XML' };
      }
      let extracted: any;
      try {
        extracted = evaluateXPath(doc, expression);
      } catch (e) {
        return { matched: false, error: `XPath failed: ${(e as Error).message}` };
      }
      if (op === 'matches' && value) {
        try { new RegExp(value); } catch (e) {
          return { matched: false, error: `Invalid regex value: ${(e as Error).message}` };
        }
      }
      if (op === 'exists') return { matched: extracted !== undefined && extracted !== null };
      const candidates = Array.isArray(extracted) ? extracted : [extracted];
      return { matched: candidates.some(compare) };
    }

    // regex: expression is the pattern tested against the raw body.
    let regex: RegExp;
    try {
      regex = new RegExp(expression);
    } catch (e) {
      return { matched: false, error: `Invalid regex: ${(e as Error).message}` };
    }
    const m = regex.test(content);
    if (op === 'exists' || op === 'matches') return { matched: m };
    if (op === 'equals') {
      // equals on a regex kind compares the first match (or whole body) to value
      const match = content.match(regex);
      const extracted = match ? (match[1] !== undefined ? match[1] : match[0]) : undefined;
      return { matched: compare(extracted) };
    }
    return { matched: m };
  } catch (e) {
    return { matched: false, error: (e as Error).message };
  }
};

export const extractData = (
    content: string,
    rules: ExtractionRule[]
): Record<string, any> => {
    const results: Record<string, any> = {};

    if (!rules || rules.length === 0) return results;
    if (!content || typeof content !== 'string') return results;

    // Cache parsed versions to avoid re-parsing for every rule
    let cachedJson: any = null;
    let jsonAttempted = false;
    let cachedXml: Document | null = null;
    let xmlAttempted = false;

    const getJson = () => {
        if (jsonAttempted) return cachedJson;
        jsonAttempted = true;
        try {
            cachedJson = JSON.parse(content);
        } catch {
            cachedJson = null;
        }
        return cachedJson;
    };

    const getXml = () => {
        if (xmlAttempted) return cachedXml;
        xmlAttempted = true;
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(content, "text/xml");
            // Check for parse errors
            if (doc.querySelector('parsererror')) {
                cachedXml = null;
            } else {
                cachedXml = doc;
            }
        } catch {
            cachedXml = null;
        }
        return cachedXml;
    };

    rules.forEach(rule => {
        try {
            let extractedValue: any = undefined;

            switch (rule.method) {
                case 'jsonpath': {
                    const json = getJson();
                    if (json) {
                        extractedValue = evaluateJsonPath(json, rule.source);
                    }
                    break;
                }

                case 'xpath': {
                    const xml = getXml();
                    if (xml) {
                        extractedValue = evaluateXPath(xml, rule.source);
                    }
                    break;
                }

                case 'regex':
                    try {
                        const regex = new RegExp(rule.source);
                        const match = content.match(regex);
                        if (match) {
                            extractedValue = match[1] !== undefined ? match[1] : match[0];
                        }
                    } catch {
                        console.warn(`Regex failed: ${rule.source}`);
                    }
                    break;

                default:
                    // Unknown extraction method — leave extractedValue undefined
                    // so the defaultValue/null fallthrough below handles the rule.
                    break;
            }

            if (extractedValue !== undefined && extractedValue !== null) {
                results[rule.targetVar] = castValue(extractedValue, rule.type);
            } else if (rule.defaultValue !== undefined && rule.defaultValue !== null && rule.defaultValue !== '') {
                results[rule.targetVar] = castValue(rule.defaultValue, rule.type);
            } else {
                results[rule.targetVar] = null;
            }

        } catch (error) {
            // Record the failure on the result set instead of silently
            // dropping it. Previously this only logged to console, which
            // made failed extractions indistinguishable from rules that
            // legitimately produced `null`.
            console.error(`Error applying rule ${rule.targetVar}`, error);
            results[rule.targetVar] = null;
        }
    });

    return results;
};
